import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { format } from 'date-fns';
import { CheckCircle2, Copy, ExternalLink, ImageUp, Loader2, X } from 'lucide-react';
import type { Event, Member, RegistrationGender, RegistrationMemberType } from '../../types';
import { ApiError } from '../../lib/api';
import { registerForEvent, type QuickRegisterResult } from '../../lib/events-api';
import { BANK_DETAILS, extraPaymentNote, PAYMENT_HELPLINE } from '../../lib/bank-details';
import { eventAmountDue, formatRupees, MY_PAYMENT_STATUS_LABELS, presentText } from '../../lib/event-utils';
import { VenueName } from './VenueName';
import {
  compressImage,
  isAcceptedScreenshotType,
  SCREENSHOT_ACCEPT,
  SCREENSHOT_MAX_BYTES,
} from '../../lib/image-compress';
import { parseIndianMobile } from '../../lib/phone';
import { cn } from '../../lib/utils';
import { MarkdownContent } from '../markdown/MarkdownContent';

const inputClass =
  'w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2.5 px-3.5 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-subtle)] transition-all duration-[var(--duration-fast)] text-sm disabled:opacity-50';

const GENDER_OPTIONS: { value: RegistrationGender; label: string }[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

type FieldErrors = Partial<Record<string, string>>;

function formatKb(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function feeChoiceLabel(name: string, amount: number | null | undefined): string {
  if (amount == null) return name;
  return `${name} · ${formatRupees(amount)}`;
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <span className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">{label}</span>
      {children}
      {error ? (
        <span className="block text-xs text-[var(--color-error)] mt-1">{error}</span>
      ) : (
        hint && <span className="block text-xs text-[var(--color-text-tertiary)] mt-1">{hint}</span>
      )}
    </div>
  );
}

function CopyValueButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(id);
  }, [copied]);

  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(
          () => setCopied(true),
          () => undefined,
        );
      }}
      className={cn(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-1)] text-[var(--color-text-tertiary)] transition-colors duration-[var(--duration-fast)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]',
        copied && 'border-[var(--color-accent)] bg-[var(--color-accent-subtle)] text-[var(--color-accent)]',
      )}
      aria-label={label}
      title={copied ? 'Copied' : label}
    >
      {copied ? <CheckCircle2 className="w-4 h-4" aria-hidden /> : <Copy className="w-4 h-4" aria-hidden />}
    </button>
  );
}

function BankDetailsCard() {
  return (
    <dl className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-1)] px-4 py-3.5 space-y-2.5">
      <div className="grid grid-cols-1 min-[401px]:grid-cols-[7.5rem_minmax(0,1fr)] gap-x-2 gap-y-0.5 items-baseline">
        <dt className="text-[0.6875rem] font-semibold tracking-[0.06em] uppercase text-[var(--color-text-tertiary)]">
          Account name
        </dt>
        <dd className="text-sm font-semibold text-[var(--color-text-primary)]">{BANK_DETAILS.name}</dd>
      </div>
      <div className="grid grid-cols-1 min-[401px]:grid-cols-[7.5rem_minmax(0,1fr)] gap-x-2 gap-y-0.5 items-baseline">
        <dt className="text-[0.6875rem] font-semibold tracking-[0.06em] uppercase text-[var(--color-text-tertiary)]">
          Bank
        </dt>
        <dd className="text-sm font-semibold text-[var(--color-text-primary)]">{BANK_DETAILS.bankName}</dd>
      </div>
      <div className="grid grid-cols-1 min-[401px]:grid-cols-[7.5rem_minmax(0,1fr)] gap-x-2 gap-y-0.5 items-baseline">
        <dt className="text-[0.6875rem] font-semibold tracking-[0.06em] uppercase text-[var(--color-text-tertiary)]">
          Branch
        </dt>
        <dd className="text-sm font-semibold text-[var(--color-text-primary)]">{BANK_DETAILS.branch}</dd>
      </div>
      <div className="grid grid-cols-1 min-[401px]:grid-cols-[7.5rem_minmax(0,1fr)] gap-x-2 gap-y-0.5 items-center">
        <dt className="text-[0.6875rem] font-semibold tracking-[0.06em] uppercase text-[var(--color-text-tertiary)]">
          Account no.
        </dt>
        <dd className="flex items-center justify-between gap-2 min-w-0 text-[0.9375rem] font-semibold font-mono tracking-wide text-[var(--color-text-primary)]">
          <span className="min-w-0 break-all select-all">{BANK_DETAILS.accountNumber}</span>
          <CopyValueButton value={BANK_DETAILS.accountNumber} label="Copy account number" />
        </dd>
      </div>
      <div className="grid grid-cols-1 min-[401px]:grid-cols-[7.5rem_minmax(0,1fr)] gap-x-2 gap-y-0.5 items-center">
        <dt className="text-[0.6875rem] font-semibold tracking-[0.06em] uppercase text-[var(--color-text-tertiary)]">
          IFSC
        </dt>
        <dd className="flex items-center justify-between gap-2 min-w-0 text-[0.9375rem] font-semibold font-mono tracking-wide text-[var(--color-text-primary)]">
          <span className="select-all">{BANK_DETAILS.ifscCode}</span>
          <CopyValueButton value={BANK_DETAILS.ifscCode} label="Copy IFSC code" />
        </dd>
      </div>
    </dl>
  );
}

function SegmentedChoice<T extends string>({
  name,
  value,
  options,
  onChange,
}: {
  name: string;
  value: T | '';
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div role="radiogroup" aria-label={name} className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          onClick={() => onChange(opt.value)}
          className={cn(
            'px-3.5 py-2 rounded-[var(--radius-md)] text-sm border transition-colors duration-[var(--duration-fast)]',
            value === opt.value
              ? 'border-[var(--color-accent)] bg-[var(--color-accent-subtle)] text-[var(--color-text-primary)] font-medium'
              : 'border-[var(--color-border)] bg-[var(--color-surface-1)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-strong)]',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function SuccessView({
  result,
  onClose,
}: {
  result: QuickRegisterResult;
  onClose: () => void;
}) {
  const { registration, whatsappGroupUrl } = result;
  return (
    <div className="px-6 py-8 text-center space-y-5">
      <CheckCircle2 className="w-12 h-12 mx-auto text-[var(--color-success)]" />
      <div>
        <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">You're registered</h3>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1">{registration.eventTitle}</p>
      </div>
      <div className="rounded-[var(--radius-lg)] bg-[var(--color-surface-2)] px-4 py-5">
        <p className="text-xs uppercase tracking-wide text-[var(--color-text-tertiary)]">Registration number</p>
        <p className="text-3xl font-semibold font-mono text-[var(--color-text-primary)] mt-1">
          {registration.regNumber}
        </p>
        <p className="text-sm text-[var(--color-text-secondary)] mt-2">
          {MY_PAYMENT_STATUS_LABELS[registration.paymentStatus]}
          {registration.amountDue != null && ` · ${formatRupees(registration.amountDue)}`}
        </p>
      </div>
      {registration.paymentStatus === 'pending' && (
        <p className="text-sm text-[var(--color-text-secondary)]">
          An admin will verify your payment screenshot. The status updates on this page.
        </p>
      )}
      <div className="flex flex-col gap-2">
        {whatsappGroupUrl && (
          <a
            href={whatsappGroupUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-[var(--radius-md)] font-medium bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] transition-colors duration-[var(--duration-fast)]"
          >
            Join the WhatsApp group
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
        <button
          type="button"
          onClick={onClose}
          className="w-full py-3 rounded-[var(--radius-md)] text-sm font-medium bg-[var(--color-surface-2)] text-[var(--color-text-primary)] hover:bg-[var(--color-border)] transition-colors duration-[var(--duration-fast)]"
        >
          Done
        </button>
      </div>
    </div>
  );
}

export function QuickRegisterDrawer({
  event,
  member,
  onClose,
  onRegistered,
}: {
  event: Event;
  member: Member;
  onClose: () => void;
  onRegistered: () => void;
}) {
  const options = event.formOptions;
  const venueName = presentText(event.location);
  const verifiedMember = member.role === 'member' || member.role === 'admin';
  const askMemberType = options.memberChoice && !verifiedMember;
  const teammateSlots = options.teamSize ? Math.max(0, options.teamSize.max - 1) : 0;
  const requiredTeammates = options.teamSize ? Math.max(0, options.teamSize.min - 1) : 0;

  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState<RegistrationGender | ''>('');
  const [memberType, setMemberType] = useState<RegistrationMemberType | ''>(
    options.memberChoice && verifiedMember ? 'member' : '',
  );
  const [teammates, setTeammates] = useState<string[]>(() => Array(teammateSlots).fill(''));
  const [notes, setNotes] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [originalSize, setOriginalSize] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [compressing, setCompressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<QuickRegisterResult | null>(null);

  const amountDue = useMemo(
    () => eventAmountDue(event, options, memberType || (options.memberChoice ? null : 'non_member')),
    [event, options, memberType],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, submitting]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const handleScreenshot = async (file: File | undefined) => {
    setErrors((prev) => ({ ...prev, paymentScreenshot: undefined }));
    if (!file) return;
    if (!isAcceptedScreenshotType(file.type)) {
      setErrors((prev) => ({ ...prev, paymentScreenshot: 'Upload a JPG, PNG, or WebP image.' }));
      return;
    }
    setCompressing(true);
    try {
      const compressed = await compressImage(file);
      if (compressed.size > SCREENSHOT_MAX_BYTES) {
        setErrors((prev) => ({ ...prev, paymentScreenshot: 'This image is too large. Try a smaller screenshot.' }));
        return;
      }
      setScreenshot(compressed);
      setOriginalSize(file.size);
      setPreviewUrl(URL.createObjectURL(compressed));
    } catch {
      setErrors((prev) => ({ ...prev, paymentScreenshot: 'Could not read this image. Try another screenshot.' }));
    } finally {
      setCompressing(false);
    }
  };

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    if (!member.phone) {
      const parsed = parseIndianMobile(phone);
      if (!parsed.ok) next.phone = parsed.error;
    }
    if (askMemberType && !memberType) next.memberType = 'Choose member or non-member.';
    if (options.collectDob && !dob) next.dob = 'Enter your date of birth.';
    if (options.collectGender && !gender) next.gender = 'Choose a gender.';
    if (options.teamSize) {
      const filled = teammates.filter((t) => t.trim()).length;
      if (filled < requiredTeammates) {
        next.teammates = `Add at least ${requiredTeammates} teammate name${requiredTeammates === 1 ? '' : 's'}.`;
      }
    }
    if (options.notesQuestion && options.notesRequired && !notes.trim()) next.notes = 'This answer is required.';
    if (options.paymentRequired && !screenshot) next.paymentScreenshot = 'Upload your payment screenshot.';
    return next;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const form = new FormData();
    if (!member.phone) form.set('phone', phone.trim());
    if (options.memberChoice && memberType) form.set('memberType', memberType);
    if (options.collectDob) form.set('dob', dob);
    if (options.collectGender && gender) form.set('gender', gender);
    if (options.teamSize) {
      form.set('teammates', JSON.stringify(teammates.map((t) => t.trim()).filter(Boolean)));
    }
    if (options.notesQuestion && notes.trim()) form.set('notes', notes.trim());
    if (options.paymentRequired && screenshot) form.set('paymentScreenshot', screenshot, screenshot.name);

    setSubmitting(true);
    try {
      const res = await registerForEvent(event.id, form);
      setResult(res);
      onRegistered();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.field) setErrors({ [err.field]: err.message });
        else setFormError(err.message);
        if (err.code === 'already_registered') onRegistered();
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const eventDate = new Date(event.date);
  const paymentNote = extraPaymentNote(options.paymentInstructions);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-end sm:items-stretch sm:justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quick-register-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close quick register"
        onClick={() => !submitting && onClose()}
      />
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        className="relative w-full sm:max-w-md max-h-[92vh] sm:max-h-none sm:h-full flex flex-col bg-[var(--color-surface-1)] rounded-t-[var(--radius-xl)] sm:rounded-none border-t sm:border-t-0 sm:border-l border-[var(--color-border)] shadow-[var(--shadow-lg)]"
      >
        <div className="px-6 py-4 border-b border-[var(--color-border)] flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <p className="text-xs font-medium text-[var(--color-accent)]">Quick register</p>
            <h2 id="quick-register-title" className="text-base font-semibold text-[var(--color-text-primary)] truncate">
              {event.title}
            </h2>
            <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
              {format(new Date(event.date), 'EEE, d MMM yyyy · p')}
              {venueName ? (
                <>
                  {' · '}
                  <VenueName name={venueName} url={event.venueMapUrl} />
                </>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="p-2 -mr-2 rounded-[var(--radius-md)] text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)]"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {result ? (
            <SuccessView result={result} onClose={onClose} />
          ) : (
            <form id="quick-register-form" onSubmit={handleSubmit} noValidate className="px-6 py-5 space-y-6">
              <section className="rounded-[var(--radius-lg)] bg-[var(--color-surface-2)] p-4 space-y-1">
                <p className="text-xs text-[var(--color-text-tertiary)]">Registering as</p>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">{member.name}</p>
                <p className="text-sm text-[var(--color-text-secondary)] break-all">{member.email}</p>
                {member.phone && <p className="text-sm text-[var(--color-text-secondary)]">{member.phone}</p>}
                <p className="text-xs text-[var(--color-text-tertiary)] pt-1">
                  From your{' '}
                  <Link to="/profile" className="underline hover:text-[var(--color-text-primary)]">
                    profile
                  </Link>
                  .
                </p>
              </section>

              {!member.phone && (
                <Field label="Mobile number" error={errors.phone} hint="Your profile has no phone number yet.">
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="98765 43210"
                    className={inputClass}
                  />
                </Field>
              )}

              {askMemberType && (
                <Field label="Membership" error={errors.memberType}>
                  <SegmentedChoice<RegistrationMemberType>
                    name="Member type"
                    value={memberType}
                    onChange={setMemberType}
                    options={[
                      { value: 'member', label: feeChoiceLabel('SMYM member', event.feeMember) },
                      { value: 'non_member', label: feeChoiceLabel('Non-member', event.feeNonMember) },
                    ]}
                  />
                </Field>
              )}

              {options.collectDob && (
                <Field
                  label="Date of birth"
                  error={errors.dob}
                  hint={
                    options.minAgeYears != null
                      ? `Minimum age ${options.minAgeYears} as on ${options.ageAsOn ?? format(eventDate, 'yyyy-MM-dd')}.`
                      : undefined
                  }
                >
                  <input
                    type="date"
                    value={dob}
                    max={format(new Date(), 'yyyy-MM-dd')}
                    onChange={(e) => setDob(e.target.value)}
                    className={inputClass}
                  />
                </Field>
              )}

              {options.collectGender && (
                <Field label="Gender" error={errors.gender}>
                  <SegmentedChoice<RegistrationGender> name="Gender" value={gender} onChange={setGender} options={GENDER_OPTIONS} />
                </Field>
              )}

              {options.teamSize && teammateSlots > 0 && (
                <Field
                  label={`Teammates (${requiredTeammates === teammateSlots ? teammateSlots : `${requiredTeammates}–${teammateSlots}`})`}
                  error={errors.teammates}
                  hint="Names of the other team members, not including you."
                >
                  <div className="space-y-2">
                    {teammates.map((name, i) => (
                      <input
                        key={i}
                        value={name}
                        onChange={(e) =>
                          setTeammates((prev) => prev.map((t, idx) => (idx === i ? e.target.value : t)))
                        }
                        placeholder={`Teammate ${i + 1}${i < requiredTeammates ? '' : ' (optional)'}`}
                        className={inputClass}
                        maxLength={100}
                      />
                    ))}
                  </div>
                </Field>
              )}

              {options.notesQuestion && (
                <Field
                  label={`${options.notesQuestion}${options.notesRequired ? '' : ' (optional)'}`}
                  error={errors.notes}
                >
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    maxLength={1000}
                    className={cn(inputClass, 'resize-y')}
                  />
                </Field>
              )}

              {options.paymentRequired && (
                <section className="space-y-4 border-t border-[var(--color-border)] pt-5">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Payment</h3>
                    <p className="text-lg font-semibold text-[var(--color-text-primary)]">
                      {askMemberType && !memberType
                        ? 'Choose member type'
                        : amountDue != null
                          ? formatRupees(amountDue)
                          : 'Amount not set'}
                    </p>
                  </div>

                  {(event.upiId || event.upiQrUrl) && (
                    <div className="flex gap-4 items-start">
                      {event.upiQrUrl && (
                        <img
                          src={event.upiQrUrl}
                          alt="UPI QR code"
                          className="w-28 h-28 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white object-contain shrink-0"
                        />
                      )}
                      {event.upiId && (
                        <div className="min-w-0">
                          <p className="text-xs font-semibold tracking-[0.06em] uppercase text-[var(--color-text-tertiary)]">
                            UPI ID
                          </p>
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[0.9375rem] font-semibold font-mono text-[var(--color-text-primary)] break-all select-all">
                              {event.upiId}
                            </p>
                            <CopyValueButton value={event.upiId} label="Copy UPI ID" />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-sm text-[var(--color-text-secondary)]">
                    Transfer to the SMYM account below, then upload the payment screenshot. Your registration is
                    confirmed once the payment is verified.
                  </p>
                  <BankDetailsCard />
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    For payment issues, WhatsApp{' '}
                    <a
                      href={`https://wa.me/91${PAYMENT_HELPLINE.digits10}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-[var(--color-accent)] underline underline-offset-2"
                    >
                      {PAYMENT_HELPLINE.display}
                    </a>
                    .
                  </p>
                  {paymentNote ? (
                    <MarkdownContent text={paymentNote} className="text-sm text-[var(--color-text-secondary)]" />
                  ) : null}

                  <Field
                    label="Payment screenshot"
                    error={errors.paymentScreenshot}
                    hint="Pay first, then upload the confirmation screen. We shrink it before upload."
                  >
                    <label
                      className={cn(
                        'flex items-center gap-3 rounded-[var(--radius-md)] border border-dashed p-3 cursor-pointer transition-colors duration-[var(--duration-fast)]',
                        errors.paymentScreenshot
                          ? 'border-[var(--color-error)]'
                          : 'border-[var(--color-border-strong)] hover:border-[var(--color-accent)]',
                      )}
                    >
                      <input
                        type="file"
                        accept={SCREENSHOT_ACCEPT}
                        className="sr-only"
                        onChange={(e) => {
                          void handleScreenshot(e.target.files?.[0]);
                          e.target.value = '';
                        }}
                        disabled={compressing || submitting}
                      />
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt="Payment screenshot preview"
                          className="w-14 h-14 rounded-[var(--radius-sm)] object-cover shrink-0"
                        />
                      ) : (
                        <span className="w-14 h-14 rounded-[var(--radius-sm)] bg-[var(--color-surface-2)] flex items-center justify-center shrink-0">
                          {compressing ? (
                            <Loader2 className="w-5 h-5 animate-spin text-[var(--color-text-tertiary)]" />
                          ) : (
                            <ImageUp className="w-5 h-5 text-[var(--color-text-tertiary)]" />
                          )}
                        </span>
                      )}
                      <span className="min-w-0 text-sm">
                        {compressing ? (
                          <span className="text-[var(--color-text-secondary)]">Compressing…</span>
                        ) : screenshot ? (
                          <>
                            <span className="block text-[var(--color-text-primary)] truncate">{screenshot.name}</span>
                            <span className="block text-xs text-[var(--color-text-tertiary)]">
                              {originalSize != null && originalSize !== screenshot.size
                                ? `${formatKb(originalSize)} → ${formatKb(screenshot.size)} · tap to replace`
                                : `${formatKb(screenshot.size)} · tap to replace`}
                            </span>
                          </>
                        ) : (
                          <span className="text-[var(--color-text-secondary)]">Choose image (JPG, PNG, WebP)</span>
                        )}
                      </span>
                    </label>
                  </Field>
                </section>
              )}

              {!options.paymentRequired && amountDue == null && (
                <p className="text-sm text-[var(--color-text-secondary)]">This event is free. Nothing else to fill in.</p>
              )}

              {formError && (
                <p className="text-sm text-[var(--color-error-text)] bg-[var(--color-error-subtle)] rounded-[var(--radius-md)] px-3 py-2">
                  {formError}
                </p>
              )}
            </form>
          )}
        </div>

        {!result && (
          <div className="px-6 py-4 border-t border-[var(--color-border)] shrink-0">
            <button
              type="submit"
              form="quick-register-form"
              disabled={submitting || compressing}
              className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-[var(--radius-md)] font-medium bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] transition-colors duration-[var(--duration-fast)] disabled:opacity-60"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? 'Registering…' : 'Confirm registration'}
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
